import { type InputType, loanOptionsConstants } from 'config'

export interface ReviewFormReport {
  id: string
  title: string
  content: string
}

export interface ReviewFormReporterSection {
  label1: string
  reports: ReviewFormReport[]
}

export interface ReviewFormReportValue {
  check: boolean
  info: string
}

export const reportCheckKey = (id: string) => `__report_check__${id}`
export const reportInfoKey = (id: string) => `__report_info__${id}`

export const buildReportInputs = (
  reports: ReviewFormReport[],
  values: Record<string, ReviewFormReportValue>,
): Record<string, InputType> => {
  const inputs: Record<string, InputType> = {}
  reports.forEach((report) => {
    const v = values?.[report.id] || { check: false, info: '' }
    inputs[reportCheckKey(report.id)] = {
      title: report.content,
      inputType: 'checkbox',
      value: v.check,
      span: 3,
    }
    inputs[reportInfoKey(report.id)] = {
      title: report.title,
      inputType: 'textarea',
      value: v.info,
      span: 3,
    }
  })
  return inputs
}

export const elingtonValuationTypes: Record<string, any> = {
  A: 'Full appraisal: 1004, 1025',
  AR: 'Review of Appraisal with grade',
  AVM: 'AVM',
  DTA: 'Desk top appraisal w/ no inspection',
  EB: 'BPO, exterior',
  HA: 'Hybrid (interior inspection x/ desk top appraisal)',
  IB: 'BPO, interior',
  IV: 'Internal value of seller',
  RAVE: 'Redbell RAVE',
  RV: 'Richer Values: AVM + interior reno review',
  MN: 'Multifamily narrative',
}

export const reversedEllingtonValuationTypes: Record<string, string> = Object.entries(elingtonValuationTypes).reduce(
  (acc, [key, value]) => {
    acc[value] = key
    return acc
  },
  {} as Record<string, string>,
)

const valuationTypeOptions = ['Appraisal', 'BPO', 'Richer Values Report', ...Object.values(elingtonValuationTypes)]

export const defaultInputs = (): Record<string, InputType> => {
  return {
    title: {
      title: 'Title',
      inputType: 'text',
      required: true,
    },
    lender: {
      title: 'Lender',
      inputType: 'text',
      required: true,
    },
    borrower: {
      title: 'Borrower/Client',
      inputType: 'text',
      required: true,
    },
    propertyAddress: {
      title: 'Property Address',
      inputType: 'map',
      required: true,
    },
    appraisalCompany: {
      title: 'Appraisal Company Name',
      inputType: 'text',
      required: true,
    },
    appraiserName: {
      title: 'Appraiser Name',
      inputType: 'text',
      required: true,
    },
    appraiserPhone: {
      title: 'Appraiser Phone',
      inputType: 'text',
      type: 'phone',
      required: true,
    },
    appraiserEmail: {
      title: 'Appraiser Email',
      inputType: 'text',
      type: 'email',
      required: true,
    },
    appraiserAddress: {
      title: 'Appraiser Address',
      inputType: 'map',
      required: true,
    },
    appraiserLicense: {
      title: 'Appraiser Licence Number',
      inputType: 'text',
      required: true,
    },
    appraiserDate: {
      title: 'Valuation Date',
      inputType: 'text',
      type: 'date',
      required: true,
    },
    appraiserAsIsProperty: {
      title: 'As Is Value',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      required: true,
    },
    appraiserAfterRepairProperty: {
      title: 'After Repair Value',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      required: true,
    },
    appraiserAIV_LTV: {
      title: 'AIV LTV',
      inputType: 'text',
      type: 'number',
      prefix: '%',
      readOnly: true,
    },
    appraiserARV_LTV: {
      title: 'ARV LTV',
      inputType: 'text',
      type: 'number',
      prefix: '%',
      readOnly: true,
    },
    appraiserLTC: {
      title: 'LTC',
      inputType: 'text',
      type: 'number',
      prefix: '%',
      readOnly: true,
    },
    appraiserLTP: {
      title: 'LTP',
      inputType: 'text',
      type: 'number',
      prefix: '%',
      readOnly: true,
    },
    appraiserManagementCompany: {
      title: 'Appraiser Management Company',
      inputType: 'text',
      required: true,
    },
    appraiserAsIsValuationType: {
      title: 'As Is Valuation Type',
      inputType: 'select',
      options: valuationTypeOptions,
      hasDefaultOption: true,
      required: true,
    },
    productCode: {
      title: 'Product Code',
      inputType: 'select',
      options: Object.keys(elingtonValuationTypes),
      hasDefaultOption: true,
    },
    appraiserMarketRent: {
      title: 'Market Rent',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      required: true,
    },
    appraiserOccupancy: {
      title: 'Occupancy',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.proposedOccupancy,
      required: true,
    },
    appraiserARVValuationType: {
      title: 'After Repair Value Valuation Type',
      inputType: 'select',
      options: valuationTypeOptions,
      hasDefaultOption: true,
      required: true,
    },
    propertyYearBuilt: {
      title: 'Property Year Built',
      inputType: 'text',
      type: 'number',
      required: true,
    },
    parcelNo: {
      inputType: 'text',
      title: 'Parcel No',
    },
    // --- Optional fields (hidden by default via Appraisal Review Fields config) ---
    pud: {
      title: 'PUD',
      inputType: 'select',
      options: ['Yes', 'No'],
      hasDefaultOption: true,
      visible: false,
    },
    hasHoa: {
      title: 'HOA',
      inputType: 'select',
      options: ['Yes', 'No'],
      hasDefaultOption: true,
      visible: false,
    },
    hoaAmount: {
      title: 'HOA Amount',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      visible: false,
    },
    legalDescription: {
      title: 'Legal Description',
      inputType: 'textarea',
      span: 3,
      visible: false,
    },
    propertyUnitCount: {
      title: 'Property Unit Count',
      inputType: 'text',
      type: 'number',
      visible: false,
    },
    newConstructionFlag: {
      title: 'New Construction Flag',
      inputType: 'checkbox',
      value: false,
      visible: false,
    },
    propertyValue: {
      title: 'Property Value',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      visible: false,
    },
    origPropValue: {
      title: 'Original Property Value',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      visible: false,
    },
    purchasePrice: {
      title: 'Purchase Price',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      visible: false,
    },
    occupyCode: {
      title: 'Occupy Code',
      inputType: 'text',
      visible: false,
    },
    origOccupancyStatus: {
      title: 'Original Occupancy Status',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.proposedOccupancy,
      visible: false,
    },
    currOccStatus: {
      title: 'Current Occupancy Status',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.proposedOccupancy,
      visible: false,
    },
    taxServCode: {
      title: 'Tax Serv Code',
      inputType: 'text',
      visible: false,
    },
    ownerOfPublicRecord: {
      title: 'Owner of Public Record',
      inputType: 'text',
      visible: false,
    },
    propertyRights: {
      title: 'Property Rights',
      inputType: 'text',
      visible: false,
    },
    assignmentType: {
      title: 'Assignment Type',
      inputType: 'text',
      visible: false,
    },
    neighborhoodCharacteristics: {
      title: 'Neighborhood Characteristics',
      inputType: 'textarea',
      span: 3,
      visible: false,
    },
    housingTrends: {
      title: 'Housing Trends',
      inputType: 'text',
      visible: false,
    },
    zoning: {
      title: 'Zoning',
      inputType: 'text',
      visible: false,
    },
    zoningCompliance: {
      title: 'Zoning Compliance',
      inputType: 'text',
      visible: false,
    },
    area: {
      title: 'Area',
      inputType: 'text',
      visible: false,
    },
    utilities: {
      title: 'Utilities',
      inputType: 'text',
      visible: false,
    },
    generalDescription: {
      title: 'General Description',
      inputType: 'textarea',
      span: 3,
      visible: false,
    },
    foundation: {
      title: 'Foundation',
      inputType: 'text',
      visible: false,
    },
    heatingAndAc: {
      title: 'Heating and AC',
      inputType: 'text',
      visible: false,
    },
    amenities: {
      title: 'Amenities',
      inputType: 'text',
      visible: false,
    },
    rooms: {
      title: 'Rooms',
      inputType: 'text',
      type: 'number',
      visible: false,
    },
    bedrooms: {
      title: 'Bedrooms',
      inputType: 'text',
      type: 'number',
      visible: false,
    },
    bathrooms: {
      title: 'Bathrooms',
      inputType: 'text',
      type: 'number',
      visible: false,
    },
    sqft: {
      title: 'Sqft',
      inputType: 'text',
      type: 'number',
      visible: false,
    },
    data: {
      inputType: 'custom',
      title: '',
      span: 3,
    },
    label1: {
      inputType: 'section',
      title: `* I have reviewed the appraisal. I do not know of any changes since the appraisal's date which would significantly affect the subject property's value.`,
      span: 3,
    },
    reviewerName: {
      title: 'Reviewer Name',
      inputType: 'text',
      type: 'text',
      required: true,
    },
    reviewedDate: {
      title: 'Reviewed Date',
      inputType: 'text',
      type: 'date',
      required: true,
    },
    isInspectProperty: {
      title: 'Did Inspect Property',
      inputType: 'checkbox',
      value: false,
    },
  }
}

/** Keys that are structural UI (not configurable field toggles). */
export const appraisalReviewNonConfigurableKeys = [
  'data',
  'label1',
  'appraiserAIV_LTV',
  'appraiserARV_LTV',
  'appraiserLTC',
  'appraiserLTP',
] as const

/** Newly added fields — default hidden until enabled in Appraisal Review Fields config. */
export const appraisalReviewOptionalFieldKeys = [
  'pud',
  'hasHoa',
  'hoaAmount',
  'legalDescription',
  'propertyUnitCount',
  'newConstructionFlag',
  'propertyValue',
  'origPropValue',
  'purchasePrice',
  'occupyCode',
  'origOccupancyStatus',
  'currOccStatus',
  'taxServCode',
  'ownerOfPublicRecord',
  'propertyRights',
  'assignmentType',
  'neighborhoodCharacteristics',
  'housingTrends',
  'zoning',
  'zoningCompliance',
  'area',
  'utilities',
  'generalDescription',
  'foundation',
  'heatingAndAc',
  'amenities',
  'rooms',
  'bedrooms',
  'bathrooms',
  'sqft',
] as const

export enum AppraisalReviewFieldSection {
  AppraisalInformation = 'AppraisalInformation',
  PropertyCharacteristics = 'PropertyCharacteristics',
  ValuationOccupancy = 'ValuationOccupancy',
  Reviewer = 'Reviewer',
}

export const AppraisalReviewFieldSectionText: Record<AppraisalReviewFieldSection, string> = {
  [AppraisalReviewFieldSection.AppraisalInformation]: 'Appraisal Information',
  [AppraisalReviewFieldSection.PropertyCharacteristics]: 'Property Characteristics',
  [AppraisalReviewFieldSection.ValuationOccupancy]: 'Valuation & Occupancy',
  [AppraisalReviewFieldSection.Reviewer]: 'Reviewer',
}

/** Field keys grouped for the Appraisal Review Fields admin config UI. */
export const appraisalReviewFieldGroups: Record<AppraisalReviewFieldSection, string[]> = {
  [AppraisalReviewFieldSection.AppraisalInformation]: [
    'title',
    'lender',
    'borrower',
    'propertyAddress',
    'appraisalCompany',
    'appraiserName',
    'appraiserPhone',
    'appraiserEmail',
    'appraiserAddress',
    'appraiserLicense',
    'appraiserDate',
    'appraiserManagementCompany',
    'appraiserAsIsValuationType',
    'productCode',
    'appraiserARVValuationType',
    'parcelNo',
  ],
  [AppraisalReviewFieldSection.PropertyCharacteristics]: [
    'propertyYearBuilt',
    'pud',
    'hasHoa',
    'hoaAmount',
    'legalDescription',
    'propertyUnitCount',
    'newConstructionFlag',
    'ownerOfPublicRecord',
    'propertyRights',
    'assignmentType',
    'neighborhoodCharacteristics',
    'housingTrends',
    'zoning',
    'zoningCompliance',
    'area',
    'utilities',
    'generalDescription',
    'foundation',
    'heatingAndAc',
    'amenities',
    'rooms',
    'bedrooms',
    'bathrooms',
    'sqft',
  ],
  [AppraisalReviewFieldSection.ValuationOccupancy]: [
    'appraiserAsIsProperty',
    'appraiserAfterRepairProperty',
    'appraiserMarketRent',
    'appraiserOccupancy',
    'propertyValue',
    'origPropValue',
    'purchasePrice',
    'occupyCode',
    'origOccupancyStatus',
    'currOccStatus',
    'taxServCode',
  ],
  [AppraisalReviewFieldSection.Reviewer]: ['reviewerName', 'reviewedDate', 'isInspectProperty'],
}

export type IAppraisalReviewFieldsConfig = Record<string, { visible?: boolean; required?: boolean }>

